import { CircularProgress, Stack, StackProps } from '@mui/material';
import { debounce } from 'lodash';
import { ReactNode, useEffect, useRef } from "react";

interface InfiniteScrollProps extends Omit<StackProps, 'children' | 'height'> {
  children: ReactNode,
  fetchMoreItems: () => Promise<any>,
  hasMore: boolean,
  height?: string,
  isLoading: boolean,
}

export const InfiniteScroll = ({
  children,
  fetchMoreItems,
  hasMore,
  height = '100vh',
  isLoading,
  ...remaining
}: InfiniteScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadMoreItems();
  }, []);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (!containerRef.current) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= (scrollHeight / 2) && hasMore && !isLoading) {
        void loadMoreItems();
      }
    }, 200);

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMore, isLoading]);

  const loadMoreItems = async () => {
    if (isLoading) {
      return;
    }
    await fetchMoreItems();
  };

  return (
    <Stack height={height} overflow="auto" ref={containerRef} {...remaining}>
      {children}
      {isLoading && (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      )}
    </Stack>
  );
};
